import React, { useState, useRef } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon as ArrowDownIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { FormSection, FormField } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import FieldRenderer from './FieldRenderer';
import SectionProperties from './SectionProperties';
import ConfirmDialog from '../common/ConfirmDialog';

interface SectionRendererProps {
  section: FormSection;
  fields: FormField[];
  selectedField?: string;
  onSelectField: (fieldId: string) => void;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onDeleteField: (fieldId: string) => void;
  onUpdateSection?: (sectionId: string, updates: Partial<FormSection>) => void;
  onDeleteSection?: (sectionId: string) => void;
  onDropField?: (fieldType: any, sectionId: string) => void;
  onMoveSectionUp?: (sectionId: string) => void;
  onMoveSectionDown?: (sectionId: string) => void;
  onMoveSection?: (dragIndex: number, hoverIndex: number) => void;
  onMoveField?: (dragIndex: number, hoverIndex: number, targetSectionId: string, sourceSectionId?: string) => void;
  sectionIndex?: number;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  isPreview?: boolean;
}

const SectionRenderer: React.FC<SectionRendererProps> = ({
  section,
  fields,
  selectedField,
  onSelectField,
  onUpdateField,
  onDeleteField,
  onUpdateSection,
  onDeleteSection,
  onDropField,
  onMoveSectionUp,
  onMoveSectionDown,
  onMoveSection,
  onMoveField,
  sectionIndex = 0,
  canMoveUp = false,
  canMoveDown = false,
  isPreview = false
}) => {
  const { t } = useLanguage();
  const [isCollapsed, setIsCollapsed] = useState(section.collapsed || false);
  const [showProperties, setShowProperties] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Drag and drop for section reordering
  const [{ isDragging }, dragRef] = useDrag({
    type: 'SECTION',
    item: { sectionIndex, sectionId: section.id },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !isPreview && !!onMoveSection,
  });

  const moveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [{ isOverSection }, dropRef] = useDrop({
    accept: 'SECTION',
    hover: (item: { sectionIndex: number, sectionId: string }, monitor) => {
      if (!onMoveSection) return;
      
      const dragIndex = item.sectionIndex;
      const hoverIndex = sectionIndex;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) return;

      // Clear existing timeout
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }

      // Throttle the move operation
      moveTimeoutRef.current = setTimeout(() => {
        onMoveSection(dragIndex, hoverIndex);
        item.sectionIndex = hoverIndex;
      }, 50);
    },
    collect: (monitor: any) => ({
      isOverSection: monitor.isOver(),
    }),
  });

  // Drop zone for fields (both new fields from palette and existing fields from other sections)
  const [{ isOver }, fieldDropRef] = useDrop({
    accept: ['FIELD', 'FORM_FIELD'],
    drop: (item: { fieldType?: { type: string }, fieldId?: string, sectionId?: string, fieldIndex?: number }, monitor) => {
      if (monitor.isOver({ shallow: true })) {
        // New field from palette
        if (item.fieldType && onDropField) {
          onDropField(item.fieldType, section.id);
          return { sectionId: section.id };
        }
        
        // Existing field from another section
        if (item.fieldId && item.sectionId && item.sectionId !== section.id && onMoveField) {
          // Move to end of target section
          const targetSectionFields = fields.length;
          // Use the correct field index from the drag item
          const dragIndex = item.fieldIndex || 0;
          onMoveField(dragIndex, targetSectionFields, section.id, item.sectionId);
          return { sectionId: section.id, moved: true };
        }
      }
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  const sortedFields = fields.sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleToggleCollapse = () => {
    if (section.collapsible) {
      setIsCollapsed(!isCollapsed);
      if (onUpdateSection) {
        onUpdateSection(section.id, { collapsed: !isCollapsed });
      }
    }
  };

  // Get section color classes
  const getSectionColorClasses = () => {
    const color = section.color || 'primary';
    
    const colorMap: Record<string, { bg: string, border: string, text: string, hover: string }> = {
      primary: {
        bg: 'bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30',
        border: 'border-primary-200 dark:border-primary-700',
        text: 'text-primary-900 dark:text-primary-100',
        hover: 'hover:from-primary-100 hover:to-primary-200 dark:hover:from-primary-800/40 dark:hover:to-primary-700/40'
      },
      blue: {
        bg: 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30',
        border: 'border-blue-200 dark:border-blue-700',
        text: 'text-blue-900 dark:text-blue-100',
        hover: 'hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-800/40 dark:hover:to-blue-700/40'
      },
      indigo: {
        bg: 'bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30',
        border: 'border-indigo-200 dark:border-indigo-700',
        text: 'text-indigo-900 dark:text-indigo-100',
        hover: 'hover:from-indigo-100 hover:to-indigo-200 dark:hover:from-indigo-800/40 dark:hover:to-indigo-700/40'
      },
      purple: {
        bg: 'bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30',
        border: 'border-purple-200 dark:border-purple-700',
        text: 'text-purple-900 dark:text-purple-100',
        hover: 'hover:from-purple-100 hover:to-purple-200 dark:hover:from-purple-800/40 dark:hover:to-purple-700/40'
      },
      pink: {
        bg: 'bg-gradient-to-r from-pink-50 to-pink-100 dark:from-pink-900/30 dark:to-pink-800/30',
        border: 'border-pink-200 dark:border-pink-700',
        text: 'text-pink-900 dark:text-pink-100',
        hover: 'hover:from-pink-100 hover:to-pink-200 dark:hover:from-pink-800/40 dark:hover:to-pink-700/40'
      },
      red: {
        bg: 'bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30',
        border: 'border-red-200 dark:border-red-700',
        text: 'text-red-900 dark:text-red-100',
        hover: 'hover:from-red-100 hover:to-red-200 dark:hover:from-red-800/40 dark:hover:to-red-700/40'
      },
      orange: {
        bg: 'bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/30',
        border: 'border-orange-200 dark:border-orange-700',
        text: 'text-orange-900 dark:text-orange-100',
        hover: 'hover:from-orange-100 hover:to-orange-200 dark:hover:from-orange-800/40 dark:hover:to-orange-700/40'
      },
      amber: {
        bg: 'bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30',
        border: 'border-amber-200 dark:border-amber-700',
        text: 'text-amber-900 dark:text-amber-100',
        hover: 'hover:from-amber-100 hover:to-amber-200 dark:hover:from-amber-800/40 dark:hover:to-amber-700/40'
      },
      yellow: {
        bg: 'bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-800/30',
        border: 'border-yellow-200 dark:border-yellow-700',
        text: 'text-yellow-900 dark:text-yellow-100',
        hover: 'hover:from-yellow-100 hover:to-yellow-200 dark:hover:from-yellow-800/40 dark:hover:to-yellow-700/40'
      },
      lime: {
        bg: 'bg-gradient-to-r from-lime-50 to-lime-100 dark:from-lime-900/30 dark:to-lime-800/30',
        border: 'border-lime-200 dark:border-lime-700',
        text: 'text-lime-900 dark:text-lime-100',
        hover: 'hover:from-lime-100 hover:to-lime-200 dark:hover:from-lime-800/40 dark:hover:to-lime-700/40'
      },
      green: {
        bg: 'bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30',
        border: 'border-green-200 dark:border-green-700',
        text: 'text-green-900 dark:text-green-100',
        hover: 'hover:from-green-100 hover:to-green-200 dark:hover:from-green-800/40 dark:hover:to-green-700/40'
      },
      emerald: {
        bg: 'bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30',
        border: 'border-emerald-200 dark:border-emerald-700',
        text: 'text-emerald-900 dark:text-emerald-100',
        hover: 'hover:from-emerald-100 hover:to-emerald-200 dark:hover:from-emerald-800/40 dark:hover:to-emerald-700/40'
      },
      teal: {
        bg: 'bg-gradient-to-r from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/30',
        border: 'border-teal-200 dark:border-teal-700',
        text: 'text-teal-900 dark:text-teal-100',
        hover: 'hover:from-teal-100 hover:to-teal-200 dark:hover:from-teal-800/40 dark:hover:to-teal-700/40'
      },
      cyan: {
        bg: 'bg-gradient-to-r from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/30',
        border: 'border-cyan-200 dark:border-cyan-700',
        text: 'text-cyan-900 dark:text-cyan-100',
        hover: 'hover:from-cyan-100 hover:to-cyan-200 dark:hover:from-cyan-800/40 dark:hover:to-cyan-700/40'
      },
      sky: {
        bg: 'bg-gradient-to-r from-sky-50 to-sky-100 dark:from-sky-900/30 dark:to-sky-800/30',
        border: 'border-sky-200 dark:border-sky-700',
        text: 'text-sky-900 dark:text-sky-100',
        hover: 'hover:from-sky-100 hover:to-sky-200 dark:hover:from-sky-800/40 dark:hover:to-sky-700/40'
      },
      violet: {
        bg: 'bg-gradient-to-r from-violet-50 to-violet-100 dark:from-violet-900/30 dark:to-violet-800/30',
        border: 'border-violet-200 dark:border-violet-700',
        text: 'text-violet-900 dark:text-violet-100',
        hover: 'hover:from-violet-100 hover:to-violet-200 dark:hover:from-violet-800/40 dark:hover:to-violet-700/40'
      },
      fuchsia: {
        bg: 'bg-gradient-to-r from-fuchsia-50 to-fuchsia-100 dark:from-fuchsia-900/30 dark:to-fuchsia-800/30',
        border: 'border-fuchsia-200 dark:border-fuchsia-700',
        text: 'text-fuchsia-900 dark:text-fuchsia-100',
        hover: 'hover:from-fuchsia-100 hover:to-fuchsia-200 dark:hover:from-fuchsia-800/40 dark:hover:to-fuchsia-700/40'
      },
      rose: {
        bg: 'bg-gradient-to-r from-rose-50 to-rose-100 dark:from-rose-900/30 dark:to-rose-800/30',
        border: 'border-rose-200 dark:border-rose-700',
        text: 'text-rose-900 dark:text-rose-100',
        hover: 'hover:from-rose-100 hover:to-rose-200 dark:hover:from-rose-800/40 dark:hover:to-rose-700/40'
      },
      slate: {
        bg: 'bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900/30 dark:to-slate-800/30',
        border: 'border-slate-200 dark:border-slate-700',
        text: 'text-slate-900 dark:text-slate-100',
        hover: 'hover:from-slate-100 hover:to-slate-200 dark:hover:from-slate-800/40 dark:hover:to-slate-700/40'
      },
      gray: {
        bg: 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/30 dark:to-gray-800/30',
        border: 'border-gray-200 dark:border-gray-700',
        text: 'text-gray-900 dark:text-gray-100',
        hover: 'hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-800/40 dark:hover:to-gray-700/40'
      },
      zinc: {
        bg: 'bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-900/30 dark:to-zinc-800/30',
        border: 'border-zinc-200 dark:border-zinc-700',
        text: 'text-zinc-900 dark:text-zinc-100',
        hover: 'hover:from-zinc-100 hover:to-zinc-200 dark:hover:from-zinc-800/40 dark:hover:to-zinc-700/40'
      },
      neutral: {
        bg: 'bg-gradient-to-r from-neutral-50 to-neutral-100 dark:from-neutral-900/30 dark:to-neutral-800/30',
        border: 'border-neutral-200 dark:border-neutral-700',
        text: 'text-neutral-900 dark:text-neutral-100',
        hover: 'hover:from-neutral-100 hover:to-neutral-200 dark:hover:from-neutral-800/40 dark:hover:to-neutral-700/40'
      },
      stone: {
        bg: 'bg-gradient-to-r from-stone-50 to-stone-100 dark:from-stone-900/30 dark:to-stone-800/30',
        border: 'border-stone-200 dark:border-stone-700',
        text: 'text-stone-900 dark:text-stone-100',
        hover: 'hover:from-stone-100 hover:to-stone-200 dark:hover:from-stone-800/40 dark:hover:to-stone-700/40'
      },
      // Light variants
      'primary-light': {
        bg: 'bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-800/20 dark:to-primary-700/20',
        border: 'border-primary-200 dark:border-primary-600',
        text: 'text-primary-800 dark:text-primary-200',
        hover: 'hover:from-primary-100 hover:to-primary-200 dark:hover:from-primary-700/30 dark:hover:to-primary-600/30'
      },
      'primary-dark': {
        bg: 'bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/40 dark:to-primary-800/40',
        border: 'border-primary-300 dark:border-primary-800',
        text: 'text-primary-900 dark:text-primary-100',
        hover: 'hover:from-primary-200 hover:to-primary-300 dark:hover:from-primary-800/50 dark:hover:to-primary-700/50'
      },
      'blue-light': {
        bg: 'bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-800/20 dark:to-blue-700/20',
        border: 'border-blue-200 dark:border-blue-600',
        text: 'text-blue-800 dark:text-blue-200',
        hover: 'hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-700/30 dark:hover:to-blue-600/30'
      },
      'blue-dark': {
        bg: 'bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40',
        border: 'border-blue-300 dark:border-blue-800',
        text: 'text-blue-900 dark:text-blue-100',
        hover: 'hover:from-blue-200 hover:to-blue-300 dark:hover:from-blue-800/50 dark:hover:to-blue-700/50'
      },
      'green-light': {
        bg: 'bg-gradient-to-r from-green-50 to-green-100 dark:from-green-800/20 dark:to-green-700/20',
        border: 'border-green-200 dark:border-green-600',
        text: 'text-green-800 dark:text-green-200',
        hover: 'hover:from-green-100 hover:to-green-200 dark:hover:from-green-700/30 dark:hover:to-green-600/30'
      },
      'green-dark': {
        bg: 'bg-gradient-to-r from-green-100 to-green-200 dark:from-green-900/40 dark:to-green-800/40',
        border: 'border-green-300 dark:border-green-800',
        text: 'text-green-900 dark:text-green-100',
        hover: 'hover:from-green-200 hover:to-green-300 dark:hover:from-green-800/50 dark:hover:to-green-700/50'
      }
    };

    // Fallback for light/dark variants
    const fallbackColor = color.replace('-light', '').replace('-dark', '');
    return colorMap[color] || colorMap[fallbackColor] || colorMap.primary;
  };

  const colorClasses = getSectionColorClasses();

  // Combine refs
  const combinedRef = (node: HTMLDivElement | null) => {
    dragRef(node);
    dropRef(node);
  };

  return (
    <div 
      ref={combinedRef}
      className={`mb-8 transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${
        isOverSection ? 'transform translate-y-1' : ''
      }`}
    >
      {/* Section Header */}
      <div
        className={`
          flex items-center justify-between p-4 
          ${colorClasses.bg}
          border ${colorClasses.border} rounded-t-lg
          ${section.collapsible ? 'cursor-pointer' : ''}
          ${!isPreview ? colorClasses.hover : ''}
          ${isDragging ? 'cursor-grabbing' : !isPreview && onMoveSection ? 'cursor-grab' : ''}
        `}
        onClick={section.collapsible ? handleToggleCollapse : undefined}
      >
        <div className="flex items-center justify-between w-full">
          {/* Left side: Move arrows + Collapse + Title */}
          <div className="flex items-center space-x-3">
            {/* Drag Handle - Always visible when not in preview */}
            {!isPreview && onMoveSection && (
              <div 
                className="flex items-center justify-center w-6 h-6 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 cursor-grab hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                title={t('formBuilder.dragToReorder') || 'Drag to reorder'}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Bars3Icon className="h-4 w-4 text-gray-500 hover:text-primary-600" />
              </div>
            )}

            {/* Collapse Button */}
            {section.collapsible && (
              <button
                type="button"
                onClick={handleToggleCollapse}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {isCollapsed ? (
                  <ChevronRightIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </button>
            )}
            
            <div>
              <h3 className={`text-lg font-semibold ${colorClasses.text}`}>
                {section.label}
              </h3>
              {section.description && (
                <p className={`text-sm ${colorClasses.text} opacity-80 mt-1`}>
                  {section.description}
                </p>
              )}
            </div>
          </div>

          {/* Right side: Edit and Delete - Always visible */}
          {!isPreview && (onUpdateSection || onDeleteSection) && (
            <div className="flex space-x-1">
              {onUpdateSection && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowProperties(true);
                  }}
                  className="p-1 text-gray-400 hover:text-primary-600"
                  title="Edit section"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              
              {onDeleteSection && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteConfirm(true);
                  }}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Delete section"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div 
          ref={fieldDropRef}
          className={`
            p-6 bg-white dark:bg-gray-900 border-x border-b border-gray-200 dark:border-gray-600 rounded-b-lg transition-all duration-200
            ${isOver ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 ring-2 ring-blue-200 dark:ring-blue-800' : ''}
            ${!isPreview ? 'min-h-[100px]' : ''}
          `}
        >
          {sortedFields.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sortedFields.map((field, index) => (
                <FieldRenderer
                  key={field.id}
                  field={field}
                  isSelected={selectedField === field.id}
                  onSelect={() => onSelectField(field.id)}
                  onUpdate={(updates) => onUpdateField(field.id, updates)}
                  onDelete={() => onDeleteField(field.id)}
                  onMoveField={onMoveField}
                  fieldIndex={index}
                  sectionId={section.id}
                  isPreview={isPreview}
                />
              ))}
            </div>
          ) : (
            <div className={`
              text-center py-8 text-gray-500 dark:text-gray-400
              ${isOver ? 'text-blue-600 dark:text-blue-400' : ''}
            `}>
              <p className="text-sm">
                {isOver 
                  ? (t('formBuilder.dropFieldHere') || 'Drop field here')
                  : (t('formBuilder.emptySection') || 'No fields in this section')
                }
              </p>
              {!isPreview && !isOver && (
                <p className="text-xs mt-1">
                  {t('formBuilder.emptySectionDescriptionCross') || 'Drag fields from palette or other sections here'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Section Properties Modal */}
      {showProperties && onUpdateSection && (
        <SectionProperties
          isOpen={showProperties}
          onClose={() => setShowProperties(false)}
          section={section}
          onUpdate={onUpdateSection}
          onMoveUp={onMoveSectionUp}
          onMoveDown={onMoveSectionDown}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => onDeleteSection && onDeleteSection(section.id)}
        title={t('formBuilder.confirmDeleteSection.title') || 'Delete Section'}
        message={t('formBuilder.confirmDeleteSection.message') || 'Are you sure you want to delete this section and all its fields? This action cannot be undone.'}
        itemName={section.label}
        confirmText={t('formBuilder.confirmDeleteSection.confirm') || 'Delete Section'}
        cancelText={t('common.buttons.cancel') || 'Cancel'}
        type="danger"
      />
    </div>
  );
};

export default SectionRenderer;
